import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BriefingData, MarketIntelligenceReport, generateMarketReport } from '../../shared/services/aiOrchestrator';
import { Sparkles, CheckCircle2, Download, TrendingUp, ShieldCheck, Building, MapPin, Search, ArrowRight, DollarSign, Calendar, Globe, Phone, FileText, Gauge } from 'lucide-react';

export const VerticalCanvasProposal: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [briefing, setBriefing] = useState<BriefingData | null>(null);
  const [report, setReport] = useState<MarketIntelligenceReport | null>(null);
  const [revenueSlider, setRevenueSlider] = useState(30000);

  useEffect(() => {
    window.scrollTo(0, 0);
    const storedBriefing = localStorage.getItem('youmachine_current_briefing');
    const storedReport = localStorage.getItem('youmachine_current_report');

    if (storedBriefing && storedReport) {
      setBriefing(JSON.parse(storedBriefing));
      setReport(JSON.parse(storedReport));
    } else {
      const fallbackBriefing: BriefingData = {
        companyName: slug ? slug.replace(/-/g, ' ').toUpperCase() : 'EMPRESA PARCEIRA',
        segment: 'Gastronomia & Serviços',
        cityState: 'Chapecó / SC',
        hasWebsite: false,
        mainGoal: 'Aumentar vendas e ter site/app próprio sem comissões',
        whatsapp: '554988416296'
      };
      setBriefing(fallbackBriefing);
      setReport(generateMarketReport(fallbackBriefing));
    }
  }, [slug]);

  if (!briefing || !report) return null;

  // ROI calculations
  const estimatedSavings = Math.round(revenueSlider * 0.15); // 15% savings on third party fees
  const estimatedSalesBoost = Math.round(revenueSlider * 0.28); // 28% sales growth

  return (
    <div className="min-h-screen bg-[#F1F5F9] text-[#0F172A] font-sans selection:bg-[#2563EB] selection:text-white py-12 px-4 print:p-0 print:bg-white">
      
      {/* Top Floating Action Bar - Hidden in Print */}
      <div className="max-w-[1080px] mx-auto mb-6 flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-300 print:hidden">
         <div className="flex items-center gap-3">
            <span className="bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase px-2.5 py-1 rounded-md border border-emerald-300">
               ✓ Proposta Gerada por IA
            </span>
            <span className="text-xs text-slate-500 font-bold">Cliente: <strong className="text-[#0F172A]">{briefing.companyName}</strong></span>
         </div>

         <div className="flex items-center gap-3">
            <button
               onClick={() => window.print()}
               className="bg-[#0F172A] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-2 hover:bg-slate-800 transition-colors"
            >
               <Download className="w-4 h-4" /> Baixar Proposta em PDF
            </button>
            <a
               href={`https://wa.me/${briefing.whatsapp}?text=${encodeURIComponent(`Olá YouMachine! Aceito a proposta comercial para a ${briefing.companyName}.`)}`}
               target="_blank"
               rel="noopener noreferrer"
               className="bg-[#2563EB] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-blue-700 transition-colors border border-blue-600"
            >
               Aprovar Proposta no WhatsApp
            </a>
         </div>
      </div>

      {/* CONTINUOUS VERTICAL CANVAS CONTAINER (~1080px × 3000px) - STRICT NO SHADOWS */}
      <div className="max-w-[1080px] mx-auto bg-white border border-slate-300 rounded-3xl overflow-hidden print:border-none print:rounded-none">
         
         {/* SECTION 1: HEADER & AUDIT - STRICT NO SHADOWS */}
         <div className="bg-slate-900 text-white p-12 lg:p-16 border-b border-slate-800 space-y-6">
            <div className="flex justify-between items-start">
               <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#2563EB] text-white flex items-center justify-center font-black text-2xl">
                     Y
                  </div>
                  <div>
                     <h1 className="font-heading font-black text-2xl text-white tracking-tight uppercase leading-none">YOUMACHINE 360°</h1>
                     <span className="text-[10px] text-blue-400 font-bold uppercase tracking-widest">Estudo de Inteligência Comercial</span>
                  </div>
               </div>
               <span className="text-xs font-mono text-slate-400">Data: {report.generatedAt}</span>
            </div>

            <div className="pt-6 border-t border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-8 items-end">
               <div>
                  <span className="text-xs text-blue-400 font-bold uppercase tracking-widest block mb-1">Apresentação Exclusiva</span>
                  <h2 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-white leading-none">
                     {briefing.companyName}
                  </h2>
                  <p className="text-xs text-slate-300 font-light mt-2">
                     Plano Estratégico de Maturação Digital, Posicionamento no Google e Aumento do Faturamento em {briefing.cityState}.
                  </p>
               </div>

               {/* Google PageSpeed Style Performance Gauges */}
               <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-4">
                  <span className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest block">Métricas de Performance Pós-Implementação</span>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                     <div className="bg-white/10 p-2.5 rounded-xl">
                        <div className="w-10 h-10 rounded-full border-2 border-emerald-400 text-emerald-400 font-black text-xs flex items-center justify-center mx-auto mb-1">98</div>
                        <span className="text-[9px] text-slate-300 block uppercase font-bold">Velocidade</span>
                     </div>
                     <div className="bg-white/10 p-2.5 rounded-xl">
                        <div className="w-10 h-10 rounded-full border-2 border-blue-400 text-blue-400 font-black text-xs flex items-center justify-center mx-auto mb-1">100</div>
                        <span className="text-[9px] text-slate-300 block uppercase font-bold">SEO Google</span>
                     </div>
                     <div className="bg-white/10 p-2.5 rounded-xl">
                        <div className="w-10 h-10 rounded-full border-2 border-amber-400 text-amber-400 font-black text-xs flex items-center justify-center mx-auto mb-1">95</div>
                        <span className="text-[9px] text-slate-300 block uppercase font-bold">Conversão</span>
                     </div>
                  </div>
               </div>
            </div>
         </div>

         {/* SECTION 2: SIMULADOR INTERATIVO DE ROI E ECONOMIA DE COMISSÕES - STRICT NO SHADOWS */}
         <div className="p-12 lg:p-16 border-b border-slate-200 space-y-8 bg-slate-50">
            <div>
               <span className="text-xs font-bold text-[#2563EB] uppercase tracking-widest">01. Calculadora Interativa de Retorno (ROI)</span>
               <h3 className="text-3xl font-heading font-black text-[#0F172A] mt-1">Simule o Aumento de Margem da {briefing.companyName}</h3>
               <p className="text-xs text-slate-600 font-light mt-1">Arraste a barra com o seu faturamento médio mensal estimado para ver o impacto direto.</p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-slate-200 space-y-6">
               <div>
                  <div className="flex justify-between items-center mb-2">
                     <span className="text-xs font-bold text-slate-700 uppercase">Faturamento Mensal Atual:</span>
                     <span className="text-2xl font-black text-[#2563EB]">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(revenueSlider)}
                     </span>
                  </div>
                  <input
                     type="range"
                     min="10000"
                     max="250000"
                     step="5000"
                     value={revenueSlider}
                     onChange={e => setRevenueSlider(Number(e.target.value))}
                     className="w-full accent-[#2563EB] h-3 bg-slate-100 rounded-lg cursor-pointer"
                  />
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  <div className="bg-emerald-50 border border-emerald-200 p-5 rounded-2xl text-[#0F172A]">
                     <span className="text-[10px] text-emerald-800 font-bold uppercase block mb-1">Economia Estimada de Comissões / Taxas</span>
                     <h4 className="text-3xl font-black text-emerald-700">
                        + {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(estimatedSavings)}/mês
                     </h4>
                     <p className="text-xs text-emerald-900 mt-1">Recuperados na sua margem bruta eliminando intermediários.</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 p-5 rounded-2xl text-[#0F172A]">
                     <span className="text-[10px] text-blue-800 font-bold uppercase block mb-1">Potencial de Vendas Adicionais (Google + Social)</span>
                     <h4 className="text-3xl font-black text-[#2563EB]">
                        + {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(estimatedSalesBoost)}/mês
                     </h4>
                     <p className="text-xs text-blue-900 mt-1">Gerados com a presença em 1º lugar no Google em {briefing.cityState}.</p>
                  </div>
               </div>
            </div>
         </div>

         {/* SECTION 3: PLANO DE AÇÃO YOUMACHINE 360° - STRICT NO SHADOWS */}
         <div className="p-12 lg:p-16 border-b border-slate-200 space-y-8 bg-white">
            <div>
               <span className="text-xs font-bold text-[#2563EB] uppercase tracking-widest">02. Ecossistema Digital YouMachine 360°</span>
               <h3 className="text-3xl font-heading font-black text-[#0F172A] mt-1">Solução Integrada de Tecnologia & Marketing</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-3">
                  <div className="flex items-center gap-2">
                     <Globe className="w-5 h-5 text-[#2563EB]" />
                     <h4 className="font-bold text-sm text-[#0F172A]">Site Institucional & Vitrine Virtual Otimizada</h4>
                  </div>
                  <p className="text-xs text-slate-600 font-light leading-relaxed">
                     Website responsivo de altíssima velocidade com carregamento instantâneo, otimizado para o Google Rank #1 em {briefing.cityState}.
                  </p>
               </div>

               <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-3">
                  <div className="flex items-center gap-2">
                     <FileText className="w-5 h-5 text-[#10B981]" />
                     <h4 className="font-bold text-sm text-[#0F172A]">Aplicativo Mobile Próprio</h4>
                  </div>
                  <p className="text-xs text-slate-600 font-light leading-relaxed">
                     Experiência de app com a marca da {briefing.companyName}, oferecendo cartão fidelidade, boletos e catálogo interativo.
                  </p>
               </div>
            </div>
         </div>

         {/* SECTION 4: MATRIZ DE PACOTES DE INVESTIMENTO - STRICT NO SHADOWS */}
         <div className="p-12 lg:p-16 bg-slate-900 text-white space-y-8">
            <div className="text-center max-w-2xl mx-auto">
               <span className="text-xs font-bold text-[#2563EB] uppercase tracking-widest">03. Proposta de Investimento Comercial</span>
               <h3 className="text-3xl font-heading font-black text-white mt-1">Escolha o Modelo Ideal para a {briefing.companyName}</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-[#0F172A]">
               
               {/* Package 1: DO OFF AO ON (PROMO) */}
               <div className="bg-white p-8 rounded-2xl border border-rose-400 space-y-4 flex flex-col justify-between relative">
                  <div className="absolute -top-3 right-6 bg-[#E11D48] text-white text-[10px] font-black uppercase px-3 py-1 rounded-full">
                     Promocional Entrada
                  </div>
                  <div>
                     <span className="text-[10px] text-rose-600 font-bold uppercase block">Plano Aceleração</span>
                     <h4 className="text-xl font-black text-[#0F172A]">DO OFF AO ON</h4>
                     <div className="my-4 py-3 border-y border-slate-100">
                        <span className="text-2xl font-black text-[#E11D48]">R$ 990,00</span>
                        <span className="text-xs text-slate-500 font-normal block">/mês nos 1ºs 6 meses</span>
                        <span className="text-[10px] text-slate-400 block mt-1">(depois R$ 1.990,00/mês na renovação)</span>
                     </div>
                     <ul className="space-y-2 text-xs text-slate-600">
                        <li>✓ Presenças Instagram & Facebook</li>
                        <li>✓ Site & Vitrine Virtual em {briefing.cityState}</li>
                        <li>✓ Cadastramos sua empresa no Google</li>
                        <li>✓ 20 Produtos no Google Shopping</li>
                     </ul>
                  </div>
                  <a href={`https://wa.me/${briefing.whatsapp}?text=${encodeURIComponent(`Olá! Quero aprovar o Plano DO OFF AO ON por R$ 990/mês para a ${briefing.companyName}.`)}`} target="_blank" rel="noopener noreferrer" className="bg-[#E11D48] text-white py-3 rounded-xl font-bold text-xs uppercase text-center block">
                     Contratar R$ 990/mês
                  </a>
               </div>

               {/* Package 2: COMPRA PROJETO */}
               <div className="bg-white p-8 rounded-2xl border border-slate-200 space-y-4 flex flex-col justify-between">
                  <div>
                     <span className="text-[10px] text-slate-400 font-bold uppercase block">Modelo Compra</span>
                     <h4 className="text-xl font-black text-[#0F172A]">Projeto Fechado</h4>
                     <div className="my-4 py-3 border-y border-slate-100">
                        <span className="text-2xl font-black text-[#0F172A]">R$ 2.000,00</span>
                        <span className="text-xs text-slate-500 font-normal block">taxa única</span>
                     </div>
                     <ul className="space-y-2 text-xs text-slate-600">
                        <li>✓ Transferência total do código-fonte</li>
                        <li>✓ Otimização SEO Local em {briefing.cityState}</li>
                        <li>✓ Manutenção opcional R$ 350/mês</li>
                     </ul>
                  </div>
                  <a href={`https://wa.me/${briefing.whatsapp}?text=${encodeURIComponent(`Olá! Quero contratar o Projeto Fechado por R$ 2.000 para a ${briefing.companyName}.`)}`} target="_blank" rel="noopener noreferrer" className="bg-[#0F172A] text-white py-3 rounded-xl font-bold text-xs uppercase text-center block">
                     Contratar Projeto Fechado
                  </a>
               </div>

               {/* Package 3: ASSINATURA 360 */}
               <div className="bg-white p-8 rounded-2xl border border-emerald-400 space-y-4 flex flex-col justify-between">
                  <div>
                     <span className="text-[10px] text-emerald-600 font-bold uppercase block">Ecossistema Completo</span>
                     <h4 className="text-xl font-black text-[#0F172A]">Serviço 360°</h4>
                     <div className="my-4 py-3 border-y border-slate-100">
                        <span className="text-2xl font-black text-[#10B981]">R$ 2.000,00</span>
                        <span className="text-xs text-slate-500 font-normal block">/mês</span>
                     </div>
                     <ul className="space-y-2 text-xs text-slate-600">
                        <li>✓ Website + App Mobile Próprio</li>
                        <li>✓ Gestão de Tráfego Pago (Meta & Google)</li>
                        <li>✓ Social Media Essencial inclusa</li>
                     </ul>
                  </div>
                  <a href={`https://wa.me/${briefing.whatsapp}?text=${encodeURIComponent(`Olá! Quero contratar o Serviço 360 por R$ 2.000/mês para a ${briefing.companyName}.`)}`} target="_blank" rel="noopener noreferrer" className="bg-[#10B981] text-white py-3 rounded-xl font-bold text-xs uppercase text-center block">
                     Contratar Serviço 360°
                  </a>
               </div>

            </div>
         </div>

      </div>

    </div>
  );
};
